import { useCallback, useEffect, useRef, type Dispatch } from 'react';

import {
  clampStageRect,
  containFit,
  defaultImageRect,
  toImageRect,
  toStageRect,
  type Size,
} from '../../lib/geometry';
import { MESSAGES, isSupportedUrl, loadImage, proxiedUrl } from '../../lib/imageSource';
import type { Action } from '../state/editor';

interface Options {
  dispatch: Dispatch<Action>;
  /** Current stage size, or null before the first measurement. */
  stage: Size | null;
}

/**
 * The two load paths from step 1, sharing one code path once a source string exists.
 *
 * Object URLs created for uploads are revoked when they are replaced and on unmount, so a
 * long session picking many files does not leak them.
 */
export function useImageLoader({ dispatch, stage }: Options): {
  loadFromUrl: (raw: string) => void;
  loadFromFile: (file: File) => void;
} {
  // Read the stage at call time rather than closing over it, so the callbacks stay stable.
  // Synced in an effect: it has committed long before a load can resolve.
  const stageRef = useRef<Size | null>(stage);
  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);

  const objectUrlRef = useRef<string | null>(null);
  // Guards against an earlier slow load resolving after a later one and winning.
  const loadIdRef = useRef(0);

  const releaseObjectUrl = useCallback((): void => {
    if (objectUrlRef.current !== null) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  useEffect(() => releaseObjectUrl, [releaseObjectUrl]);

  const load = useCallback(
    (src: string): void => {
      const id = (loadIdRef.current += 1);
      dispatch({ type: 'loadStarted' });

      loadImage(src)
        .then((element) => {
          if (loadIdRef.current !== id) return;

          const natural: Size = { w: element.naturalWidth, h: element.naturalHeight };

          // 84% of the picture, centred on it — stage-independent (see defaultImageRect).
          // Clamp against the stage when one has been measured; that only changes anything
          // for extreme panoramas, where the fitted picture is shorter than the minimum board.
          let board = defaultImageRect(natural);
          const currentStage = stageRef.current;
          if (currentStage !== null) {
            const fit = containFit(currentStage, natural);
            board = toImageRect(clampStageRect(toStageRect(board, fit), currentStage), fit);
          }

          dispatch({ type: 'loaded', picture: { src, natural, element }, board });
        })
        .catch((cause: unknown) => {
          if (loadIdRef.current !== id) return;
          dispatch({
            type: 'failed',
            message: cause instanceof Error ? cause.message : MESSAGES.badUrl,
          });
        });
    },
    [dispatch],
  );

  const loadFromUrl = useCallback(
    (raw: string): void => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        dispatch({ type: 'failed', message: MESSAGES.emptyUrl });
        return;
      }
      if (!isSupportedUrl(trimmed)) {
        dispatch({ type: 'failed', message: MESSAGES.badUrl });
        return;
      }

      releaseObjectUrl();
      load(proxiedUrl(trimmed));
    },
    [dispatch, load, releaseObjectUrl],
  );

  const loadFromFile = useCallback(
    (file: File): void => {
      releaseObjectUrl();
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      load(url);
    },
    [load, releaseObjectUrl],
  );

  return { loadFromUrl, loadFromFile };
}
